import { Checkbox, Tooltip } from 'antd';
import classNames from 'classnames';
import Lottie from 'react-lottie';
import { useEffect, useMemo, useRef, useState } from 'react';
import GeneratingSummary from 'src/modules/ambient_solo/components/PastEncounter/GeneratingSummary.json';
import { ScreenLoader, SummaryCard } from 'src/components';
import {
  useGetEhrAppointments,
  useGeneratePreChartSummary,
} from 'src/hooks/ehr';
import {
  useGetEncounterDetails,
  useUpdateEncounterDetails,
  useLegacyEncounterStore,
  useCompleteEncounter,
  useRegenerateEncounterSummary,
} from 'src/hooks/encounter';
import { parseSummary } from 'src/utils';
import { PreChartDocuments } from './preChartDocuments';
import {
  useGetCurrentPractitioner,
  useGetCurrentPractitionerSettings,
  useUpdateCurrentPractitionerSettings,
} from 'src/hooks/settings';
import {
  Button,
  Card,
  Segmented,
  Status,
  toast,
} from '@insight-health-ai/roseate/components';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Microphone01,
  RefreshCcw05,
  Star06,
} from '@insight-health-ai/roseate/icons';
import { ExclamationCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { RecordTranscription } from './recordTranscription';
import dayjs from 'dayjs';
import { useTheme } from '@insight-health-ai/roseate/theme';
import {
  useCreateSummaryVersion,
  useGetSummaryList,
  useUpdateSummary,
} from 'src/hooks/summary';
import { lightenColor, useScreenSize } from '@insight-health-ai/roseate/utils';
import { analytics } from 'src/services';
import { useAuth } from '@frontegg/react';
import TemplateSelectDropdown from 'src/modules/ambient_solo/components/Settings/Settings.TemplateDropdown';
import { PractitionerSettings } from 'src/models';
import { noop } from 'lodash';

export type EncounterPreChartProps = {
  encounterId: string;
  mode: 'vca' | 'aura';
  selectedMicrophone: string; // todo: refactor this to in independent store
  legacySearchFilters: unknown;
};

const SummaryTypeOptions = [
  {
    label: 'Standard view',
    value: 'standard',
  },
  {
    label: 'Template view',
    value: 'template',
  },
] as const;

const sectionsOrder = [
  'patient profile',
  'last visit',
  'treatment history',
  'data',
  'background',
  'diagnosis',
];

export const EncounterPreChart = ({
  encounterId,
  selectedMicrophone,
  mode,
  // @todo: Harshit | remove this post migration
  legacySearchFilters,
}: EncounterPreChartProps) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [recording, setRecording] = useState<{
    active: boolean;
    since: number | null;
  }>({
    active: false,
    since: null,
  });

  const { mediaMax } = useScreenSize();
  const isMobile = mediaMax('xs');
  const recordTranscriptionRef = useRef<{
    stopRecording: () => Promise<void>;
  }>(null);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [selectedSummaryMode, setSelectedSummaryMode] =
    useState<PractitionerSettings['ambient_pre_chart_summary_mode']>(null);
  const [changeDetected, setChangeDetected] = useState(false);
  const [hasDocuments, setHasDocuments] = useState<boolean>(false);
  const [hasTranscript, setHasTranscript] = useState<boolean>(false);
  // const encounterChangedLocalStorageKey = `encounter:${encounterId}:preChart:lastChanged`;
  const [selectedTemplates, setSelectedTemplates] = useState<{
    templateId: string | null;
    templateOverridesId: string | null;
  } | null>(null);

  const { data: currentPractitioner } = useGetCurrentPractitioner();
  const {
    data: currentPractitionerSettings,
    isLoading: isLoadingCurrentPractitionerSettings,
  } = useGetCurrentPractitionerSettings();

  const {
    mutateAsync: updateCurrentPractitionerSettings,
    isPending: isUpdatingCurrentPractitionerSettings,
  } = useUpdateCurrentPractitionerSettings();

  const { data: encounterDetails, isLoading: isLoadingEncounterDetails } =
    useGetEncounterDetails(
      {
        encounterId,
      },
      {
        refetchInterval: (query) =>
          query.state.data?.pre_chart_status === 'summary_inprogress'
            ? 3000
            : false,
      }
    );
  const {
    mutateAsync: updateEncounterDetails,
    isPending: isUpdatingEncounterDetails,
  } = useUpdateEncounterDetails();
  const { mutateAsync: completeEncounter, isPending: isCompletingEncounter } =
    useCompleteEncounter();
  const { updateEncounterList } = useLegacyEncounterStore();
  const { mutateAsync: regenerateEncounterSummary } =
    useRegenerateEncounterSummary();

  const {
    data: summaryList,
    isLoading: isLoadingSummaryList,
    refetch: refetchSummaryList,
  } = useGetSummaryList({
    encounterId,
    encounterPhase: 'pre-chart',
    type: 'ambient',
  });
  const {
    mutateAsync: createSummaryVersion,
    isPending: isCreatingSummaryVersion,
  } = useCreateSummaryVersion();

  const { mutateAsync: updateSummary, isPending: isUpdatingSummary } =
    useUpdateSummary();

  const busyUpdating =
    isUpdatingSummary ||
    isCreatingSummaryVersion ||
    isUpdatingEncounterDetails ||
    isCompletingEncounter;

  const { data: appointmentsData, isLoading: isLoadingAppointments } =
    useGetEhrAppointments(
      {
        patientId: encounterDetails?.patient_id ?? 0,
        count: 1,
        practitionerId: encounterDetails?.practitioner_id ?? 0,
        appointmentId: encounterDetails?.appointment_id ?? 0,
      },
      {
        enabled: !!(
          encounterDetails &&
          encounterDetails.patient_id &&
          encounterDetails.appointment_id &&
          encounterDetails?.encounter_ehr_data?.appointment_ehr_id &&
          currentPractitioner?.org_settings?.ehr_precharting_enabled
        ),
      }
    );

  const { mutateAsync: generatePreChartSummary } = useGeneratePreChartSummary();

  const summary = summaryList?.summaries?.[0];
  const clinicalNote = appointmentsData?.clinical_notes?.[0];
  const appointmentStartTime = appointmentsData?.appointments?.[0]?.start_date;

  const detectedSummaryMode: Extract<
    PractitionerSettings['ambient_pre_chart_summary_mode'],
    string
  > =
    summary?.meta?.mode ||
    (!!summary?.summary_json &&
    !(summary?.summary_json || '').trim().startsWith('{')
      ? 'template'
      : 'standard');

  const summaryModePreference = useMemo(() => {
    if (selectedSummaryMode) {
      return selectedSummaryMode;
    }

    if (summary) {
      return detectedSummaryMode;
    }

    return (
      currentPractitionerSettings?.ambient_pre_chart_summary_mode || 'standard'
    );
  }, [
    currentPractitionerSettings?.ambient_pre_chart_summary_mode,
    selectedSummaryMode,
    !!summary,
    detectedSummaryMode,
  ]);

  const templatePrefernces = useMemo(() => {
    if (
      selectedTemplates?.templateId ||
      selectedTemplates?.templateOverridesId
    ) {
      return selectedTemplates;
    }

    if (
      encounterDetails?.pre_chart_template_id ||
      encounterDetails?.pre_chart_template_overrides_id
    ) {
      return {
        templateId: encounterDetails?.pre_chart_template_id,
        templateOverridesId: encounterDetails?.pre_chart_template_overrides_id,
      };
    }

    return {
      templateId: currentPractitionerSettings?.ambient_pre_chart_template_id,
      templateOverridesId:
        currentPractitionerSettings?.ambient_pre_chart_template_overrides_id,
    };
  }, [selectedTemplates, encounterDetails, currentPractitionerSettings]);

  const { summaryJson, summaryMode, notesJson, sectionsToRender } =
    useMemo(() => {
      let summaryMode = detectedSummaryMode;

      let summaryHtml = '';
      let summaryJson = {} as Record<string, string>;

      if (summary?.summary_json && summaryMode === 'standard') {
        try {
          summaryJson = JSON.parse(summary?.summary_json || '{}');
        } catch (error) {
          summaryMode = 'template';
        }
      }

      if (summary?.summary_json && summaryMode === 'template') {
        summaryHtml = parseSummary(summary?.summary_json || '');
      }

      let notesJson = {} as Record<string, string>;
      if (clinicalNote?.content) {
        try {
          notesJson = JSON.parse(clinicalNote?.content || '{}');
        } catch (error) {
          // ignore
        }
      }

      return {
        summaryJson,
        summaryHtml,
        summaryMode,
        notesJson,
        sectionsToRender: Object.entries({ ...notesJson, ...summaryJson })
          .map(([key, value]) => ({
            title: key,
            content: value as string,
            key,
            selected: !!summaryJson[key],
          }))
          .sort((a, b) => {
            const aIndex = sectionsOrder.indexOf(a.title.toLowerCase());
            const bIndex = sectionsOrder.indexOf(b.title.toLowerCase());
            // If both are not found, keep their original order
            if (aIndex === -1 && bIndex === -1) return 0;
            // If only a is not found, it comes after b
            if (aIndex === -1) return 1;
            // If only b is not found, it comes after a
            if (bIndex === -1) return -1;
            // Otherwise, sort by their index
            return aIndex - bIndex;
          }),
      };
    }, [clinicalNote?.content, summary?.summary_json]);

  useEffect(() => {
    if (encounterDetails?.pre_chart_status === 'completed') {
      refetchSummaryList();
    }
  }, [encounterDetails?.pre_chart_status === 'summary_inprogress']);

  const allowSectionSelection = !!(
    summaryMode === 'standard' && Object.keys(notesJson).length
  );

  const toggleSummarySections = async (sections: Record<string, boolean>) => {
    const newSections = {
      ...Object.keys(summaryJson).reduce(
        (acc, key) => {
          acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>
      ),
      ...sections,
    };

    const newSummaryJson = Object.fromEntries(
      sectionsToRender
        .filter(({ key }) => newSections[key])
        .map(({ key, content }) => [key, content])
    );

    try {
      if (summary) {
        await updateSummary({
          summaryId: summary?.summary_id!,
          payload: {
            summary_json: JSON.stringify(newSummaryJson),
          },
        });
      } else {
        await createSummaryVersion({
          encounterId,
          encounterPhase: 'pre-chart',
          version: 1,
          summaryJson: JSON.stringify(newSummaryJson),
        });
        await updateEncounterDetails({
          encounterId,
          encounterPhase: 'pre-chart',
          payload: {
            pre_chart_status: 'inprogress',
          },
        });
        /**
         * @todo: Harshit | remove this post migration
         */
        try {
          updateEncounterList(legacySearchFilters, {
            currentEncounterId: encounterId,
          });
        } catch (error) {
          // do nothing
        }
      }
    } catch {
      toast.error('Failed to update section(s)');
    }
  };

  const generateNotes = async () => {
    analytics.track(
      `Clicked Generate Pre-charting In Ambient From ${mode === 'vca' ? 'VCA' : 'Aura'}`,
      {
        email: user?.email,
      }
    );

    try {
      if (recording.active) {
        await recordTranscriptionRef.current?.stopRecording?.();
      }

      if (summaryModePreference === 'template') {
        await updateEncounterDetails({
          encounterId,
          encounterPhase: 'pre-chart',
          payload: {
            pre_chart_template_id: templatePrefernces.templateId,
            pre_chart_template_overrides_id:
              templatePrefernces.templateOverridesId,
          },
        });
        if (summary) {
          await regenerateEncounterSummary({
            encounterId,
            template_id: templatePrefernces.templateId,
            template_overrides_id: templatePrefernces.templateOverridesId,
            encounterPhase: 'pre-chart',
          });
        } else {
          await completeEncounter({
            encounterId,
            encounterPhase: 'pre-chart',
          });
        }
      } else {
        await generatePreChartSummary({
          encounterId,
          clinicalNoteId: clinicalNote?.note_id,
        });
      }

      setChangeDetected(false);
      toast.success('Generated note will be available shortly.');
    } catch {
      toast.error('Something went wrong while generating note');
    }
  };

  if (
    isLoadingEncounterDetails ||
    isLoadingAppointments ||
    isLoadingSummaryList ||
    isLoadingCurrentPractitionerSettings
  ) {
    return <ScreenLoader />;
  }

  if (encounterDetails?.pre_chart_status === 'summary_inprogress') {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <Lottie
          options={{
            loop: true,
            animationData: GeneratingSummary,
            rendererSettings: {
              preserveAspectRatio: 'xMidYMid meet',
            },
          }}
          style={{ maxWidth: 400 }}
        />
      </div>
    );
  }

  const renderStandardView = () => {
    return (
      sectionsToRender.length > 0 && (
        <div className="flex flex-col gap-2 pb-4">
          <div className="flex justify-between items-center gap-4">
            <div className="flex flex-col">
              <div className="font-medium">Chart summary</div>
              {appointmentStartTime && (
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Calendar />
                  Last appointment:{' '}
                  {dayjs(appointmentStartTime).format('MMM D, YYYY h:mm A')}
                </span>
              )}
            </div>
            {allowSectionSelection && (
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-2 font-medium">
                  <Checkbox
                    checked={Object.values(sectionsToRender).every(
                      ({ selected }) => selected
                    )}
                    disabled={busyUpdating}
                    onChange={(e) => {
                      toggleSummarySections(
                        sectionsToRender.reduce(
                          (acc, section) => {
                            acc[section.key] = e.target.checked;
                            return acc;
                          },
                          {} as Record<string, boolean>
                        )
                      );
                    }}
                  >
                    Include all sections to In visit
                  </Checkbox>
                </div>
                <div className="text-sm text-gray-500 text-right">
                  {sectionsToRender.filter(({ selected }) => selected).length}{' '}
                  of {sectionsToRender.length} included
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {sectionsToRender.map((section) => (
              <div
                key={section.title}
                className={classNames(
                  'flex flex-col rounded-lg border border-primary',
                  {
                    'bg-green-100/30': section.selected,
                  }
                )}
              >
                <div
                  className={classNames(
                    'flex gap-2 items-center border-b p-4 shadow-sm',
                    {
                      'cursor-not-allowed':
                        allowSectionSelection && busyUpdating,
                      'cursor-pointer': allowSectionSelection && !busyUpdating,
                    }
                  )}
                  onClick={() => {
                    if (busyUpdating || !allowSectionSelection) return;

                    toggleSummarySections({
                      [section.key]: !section.selected,
                    });
                  }}
                >
                  {allowSectionSelection && (
                    <Checkbox
                      checked={section.selected}
                      disabled={busyUpdating}
                    />
                  )}
                  <div className="font-medium">{section.title}</div>
                  <div className="ml-auto right-0">
                    {section.selected ? (
                      <Status
                        size="xs"
                        label="Included"
                        style={{
                          backgroundColor: theme.colors.success.DEFAULT,
                          color: '#ffffff',
                        }}
                      />
                    ) : (
                      <Tooltip
                        title={
                          !allowSectionSelection ? (
                            <div className="flex flex-wrap overflow-wrap">
                              Press Regenerate to add to the summary.
                            </div>
                          ) : null
                        }
                        placement="topRight"
                      >
                        <Status
                          size="xs"
                          label="Not Included"
                          appearance="warning"
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 px-4 py-2">
                  <div
                    className="text-sm"
                    dangerouslySetInnerHTML={{
                      __html: parseSummary(section.content),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    );
  };

  const renderTemplateView = () => {
    if (!summary) {
      return null;
    }

    return (
      <SummaryCard
        filters={{
          encounterId,
          encounterPhase: 'pre-chart',
        }}
        getSummary={() => summary}
        editable={true}
        editorProps={{
          showToolbar: false,
          minHeight: '250px',
        }}
      />
    );
  };

  const summaryViewJsx =
    summaryMode === 'standard' ? renderStandardView() : renderTemplateView();

  const renderAudioControls = () => {
    if (recording.active) {
      return (
        <div className="flex items-center gap-2">
          <Status size="xs" label="◦ Recording" appearance="error" />
        </div>
      );
    }

    if (transcriptExpanded || !summaryViewJsx) {
      return null;
    }
    return (
      <Button
        variant="outlined"
        appearance="primary"
        icon={<Microphone01 />}
        onClick={() => setTranscriptExpanded(true)}
      >
        {isMobile ? null : 'Record pre-chart'}
      </Button>
    );
  };

  const templateViewChanged =
    summaryViewJsx &&
    summaryModePreference &&
    summaryModePreference !== summaryMode;
  const shouldShowTooltip =
    !recording?.active &&
    !isDocumentsModalOpen &&
    (templateViewChanged || changeDetected);

  const renderRegenerateButton = () => {
    if (!summary && !hasDocuments && !hasTranscript) {
      return null;
    }

    return (
      <Tooltip
        placement="topRight"
        styles={{
          body: {
            backgroundColor: lightenColor(theme.colors.warning.DEFAULT, 90),
          },
        }}
        open={shouldShowTooltip}
        title={
          <div className="flex gap-2 items-center">
            <ExclamationCircleFilled
              style={{
                color: theme.colors.warning.DEFAULT,
                fontSize: theme.fontSize.lg[0],
              }}
            />
            <div className="flex flex-col">
              <div className="font-medium">Detected Changes</div>
              <div className="text-xs">
                {summary
                  ? 'Regenerate to view updated summary.'
                  : 'Click Generate to view the summary.'}
              </div>
            </div>
          </div>
        }
      >
        <Button
          appearance="primary"
          icon={summary ? <RefreshCcw05 /> : <Star06 />}
          onClick={generateNotes}
        >
          {summary ? 'Regenerate' : 'Generate'}
        </Button>
      </Tooltip>
    );
  };

  return (
    <div className="h-full w-full px-2">
      <div className="flex flex-col gap-5 max-w-2xl mx-auto">
        <div className="flex flex-col gap-2 w-full sticky top-0 z-10 bg-white">
          <Card
            className="shadow"
            padding={{ header: 'none', body: 'none' }}
            title={
              <div
                className="flex items-center justify-between w-full p-2"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setTranscriptExpanded(!transcriptExpanded);
                  }
                }}
              >
                {summaryViewJsx && (
                  <Button
                    variant="text"
                    appearance="secondary"
                    icon={transcriptExpanded ? <ChevronUp /> : <ChevronDown />}
                    onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                  />
                )}
                <div className="flex gap-2 w-full justify-end flex-wrap flex-1">
                  {renderAudioControls()}
                  <div>
                    <PreChartDocuments
                      isCompact={isMobile}
                      encounterId={encounterId}
                      source="pre_chart_history"
                      onDocumentsChange={() => {
                        setChangeDetected(true);
                      }}
                      onChange={({ documents }) => {
                        setHasDocuments(documents.length > 0);
                      }}
                      onOpenChange={(isOpen) => {
                        setIsDocumentsModalOpen(isOpen);
                      }}
                    />
                  </div>
                  {renderRegenerateButton()}
                </div>
              </div>
            }
          >
            <RecordTranscription
              ref={recordTranscriptionRef}
              mode={mode}
              isExpanded={!summaryViewJsx || transcriptExpanded}
              status={encounterDetails?.pre_chart_status ?? 'new'}
              encounterId={encounterId}
              encounterPhase="pre-chart"
              selectedMicrophone={selectedMicrophone}
              onRecordingChange={setRecording}
              onTranscriptionChange={() => {
                setChangeDetected(true);
              }}
              onChange={(transcription) => {
                setHasTranscript(transcription.length > 0);
              }}
              legacySearchFilters={legacySearchFilters}
            />
          </Card>
        </div>

        {summary && (
          <div className="font-normal flex items-center gap-2 flex-wrap">
            <Segmented
              shape="round"
              options={SummaryTypeOptions as any}
              value={summaryModePreference}
              onChange={(value) =>
                setSelectedSummaryMode(value as typeof selectedSummaryMode)
              }
            />
            {(!currentPractitionerSettings?.ambient_pre_chart_summary_mode ||
              summaryModePreference !==
                currentPractitionerSettings?.ambient_pre_chart_summary_mode) && (
              <span
                className={classNames(
                  'text-xs text-info hover:cursor-pointer hover:underline flex items-center gap-1',
                  {
                    'opacity-50 cursor-not-allowed':
                      isUpdatingCurrentPractitionerSettings,
                  }
                )}
                onClick={async () => {
                  try {
                    await updateCurrentPractitionerSettings({
                      ambient_pre_chart_summary_mode: summaryModePreference,
                    });
                    toast.success('Default pre-chart view is saved');
                  } catch {
                    toast.error('Failed to update preferences');
                  }
                }}
              >
                {isUpdatingCurrentPractitionerSettings && <LoadingOutlined />}
                Set as default
              </span>
            )}

            <div className="flex items-center sm:ml-auto">
              {summaryModePreference === 'template' && (
                <TemplateSelectDropdown
                  selectProps={{
                    popupMatchSelectWidth: false,
                  }}
                  setNextWorkingTemplate={noop}
                  encounterPhase="pre-chart"
                  currentTemplate={{
                    template_id: templatePrefernces.templateId,
                    template_overrides_id:
                      templatePrefernces.templateOverridesId,
                  }}
                  // @ts-ignore
                  templateId={templatePrefernces.templateId}
                  // @ts-ignore
                  templateOverridesId={templatePrefernces.templateOverridesId}
                  setCurrentTemplate={(template: any) => {
                    setSelectedTemplates({
                      templateId: template.template_id || null,
                      templateOverridesId:
                        template.template_overrides_id || null,
                    });
                  }}
                />
              )}
            </div>
          </div>
        )}
        {summaryViewJsx && (
          <div className="relative">
            {templateViewChanged && (
              <div className="z-[1] bg-white/95 absolute top-0 left-0 w-full h-full flex justify-center py-8">
                <div className="flex flex-col gap-2 text-gray-500">
                  <div className="flex items-center justify-center gap-2 text-lg font-medium">
                    <RefreshCcw05 /> Regenerate
                  </div>
                  <div className="text-gray-500 text-center">
                    This summary is outdated and doesn't reflect your new
                    template selection.
                  </div>
                </div>
              </div>
            )}
            {summaryViewJsx}
          </div>
        )}
      </div>
    </div>
  );
};
